import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { usePublicOrganizationDevices } from "@/hooks/use-public-organization-devices";
import { PublicDeviceTile } from "@/components/devices/PublicDeviceTile";
import { PublicDeviceFilters, PublicDeviceFilterState } from "@/components/devices/PublicDeviceFilters";
import { GridLayoutSelector } from "@/components/devices/GridLayoutSelector";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, AlertCircle } from "lucide-react";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { filterAndSortPublicDevices } from "@/utils/publicDeviceFilters";
import { showcaseClient } from "@/integrations/supabase/showcase-client";
import { ShowcaseFeedbackButton } from "@/components/devices/ShowcaseFeedbackButton";
import { ShowcaseDeviceMap } from "@/components/devices/ShowcaseDeviceMap";
import { PublicDeviceDetailModal } from "@/components/devices/PublicDeviceDetailModal";
import { ShowcaseAccessGate } from "@/components/devices/ShowcaseAccessGate";
import { hasStoredShowcaseAccess, showcaseCodesMatch, storeShowcaseAccess } from "@/utils/showcaseAccess";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

function OrganizationShowcaseContent() {
  console.log('🎬 [OrganizationShowcase] ========== COMPONENT MOUNTED ==========');
  console.log('🎬 [OrganizationShowcase] Timestamp:', new Date().toISOString());
  console.log('🎬 [OrganizationShowcase] Window location:', window.location.href);
  
  const { organizationId } = useParams<{ organizationId: string }>();
  const { brand, isWhiteLabel } = useBrand();
  console.log('🎬 [OrganizationShowcase] Organization ID from params:', organizationId);
  console.log('🎬 [OrganizationShowcase] Params type:', typeof organizationId);
  console.log('🎬 [OrganizationShowcase] Params length:', organizationId?.length);
  
  const { data, isLoading, error } = usePublicOrganizationDevices(organizationId || "");
  
  const [filters, setFilters] = useState<PublicDeviceFilterState>({
    search: '',
    sortBy: 'recent',
  });
  
  const [gridColumns, setGridColumns] = useState(3);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Optional access code gate
  const expectedAccessCode = (data?.organization as any)?.showcase_access_code as string | null | undefined;
  const accessCodeEnabled = !!(data?.organization as any)?.showcase_access_code_enabled && !!expectedAccessCode;
  const [unlocked, setUnlocked] = useState(false);
  const isGated = accessCodeEnabled && !unlocked;

  useEffect(() => {
    const orgId = data?.organization?.id;
    if (!orgId || !accessCodeEnabled) return;

    // Already unlocked on this device?
    if (hasStoredShowcaseAccess(orgId, expectedAccessCode)) {
      setUnlocked(true);
      return;
    }

    // Shareable link: /showcase/slug?code=XXXXXX
    const urlCode = new URLSearchParams(window.location.search).get('code');
    if (urlCode && showcaseCodesMatch(urlCode, expectedAccessCode)) {
      storeShowcaseAccess(orgId, urlCode);
      setUnlocked(true);
    }
  }, [data?.organization?.id, accessCodeEnabled, expectedAccessCode]);

  // Track page-level view (once per session)
  useEffect(() => {
    if (!data?.organization?.id || isGated) return;

    const sessionKey = `showcase_page_tracked_${data.organization.id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const params = new URLSearchParams(window.location.search);
    const timer = setTimeout(async () => {
      try {
        await showcaseClient.functions.invoke('track-showcase-view', {
          body: {
            organization_id: data.organization.id,
            utm_source: params.get('utm_source'),
            utm_medium: params.get('utm_medium'),
            utm_campaign: params.get('utm_campaign'),
          }
        });
        sessionStorage.setItem(sessionKey, 'true');
        console.log('📊 [OrganizationShowcase] Page view tracked');
      } catch (e) {
        console.error('Failed to track page view:', e);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [data?.organization?.id, isGated]);

  // Track views for all devices on showcase page
  useEffect(() => {
    if (!data?.organization?.id || !data?.devices || data.devices.length === 0 || isGated) {
      return;
    }

    const sessionKey = `showcase_tracked_${data.organization.id}`;
    const alreadyTracked = sessionStorage.getItem(sessionKey);
    
    if (alreadyTracked) {
      console.log('🎯 [OrganizationShowcase] Views already tracked in this session');
      return;
    }

    // Track all devices
    const trackAllDevices = async () => {
      console.log(`🎯 [OrganizationShowcase] Tracking views for ${data.devices.length} devices`);
      
      const trackPromises = data.devices.map(async (device) => {
        try {
          await showcaseClient.functions.invoke('track-device-view', {
            body: { 
              device_id: device.device_id, 
              organization_id: data.organization.id 
            }
          });
          console.log(`✅ [OrganizationShowcase] Tracked view for ${device.device_name}`);
        } catch (error) {
          console.error(`❌ [OrganizationShowcase] Error tracking ${device.device_name}:`, error);
        }
      });

      await Promise.all(trackPromises);
      sessionStorage.setItem(sessionKey, 'true');
      console.log('🎉 [OrganizationShowcase] All device views tracked');
    };

    // Track after a short delay to ensure user is actually viewing
    const timer = setTimeout(trackAllDevices, 2000);
    return () => clearTimeout(timer);
  }, [data?.organization?.id, data?.devices, isGated]);
  
  console.log('🎬 [OrganizationShowcase] Hook state:', { 
    hasData: !!data, 
    isLoading, 
    hasError: !!error,
    errorType: error ? typeof error : 'none'
  });
  
  console.log('🎬 [OrganizationShowcase] Hook returned:', { 
    hasData: !!data, 
    isLoading, 
    hasError: !!error,
    errorMessage: error instanceof Error ? error.message : error,
    organizationName: data?.organization?.name,
    devicesCount: data?.devices?.length
  });

  useEffect(() => {
    console.log('🎬 [OrganizationShowcase] Component mounted', { organizationId });
    console.log('🎬 [OrganizationShowcase] Query state:', { data, isLoading, error });
  }, [organizationId, data, isLoading, error]);

  if (isLoading) {
    console.log('🎬 [OrganizationShowcase] Rendering loading state');
    return (
      <div className="brand-showcase min-h-screen bg-background">
        {/* Header Skeleton */}
        <div className="border-b bg-card shadow-sm">
          <div className="container mx-auto px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-96" />
              </div>
              <Skeleton className="h-12 w-32" />
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="container mx-auto px-4 md:px-8 py-6">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-32 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    console.error('🎬 [OrganizationShowcase] Error state:', error);
    console.error('🎬 [OrganizationShowcase] Data status:', { hasData: !!data });
    return (
      <div className="brand-showcase min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-8 p-8 max-w-2xl">
          {/* Snapshot Live Logo */}
          <div className="flex justify-center">
            <img
              src={isWhiteLabel && brand?.config.logo_url ? brand.config.logo_url : "/lovable-uploads/37267525-cc1e-42cd-a617-eb98609d4050.png"}
              alt={isWhiteLabel ? (brand?.config.company_name || "Brand logo") : "Snapshot Live Logo"}
              className={isWhiteLabel ? "brand-showcase-large-logo w-auto object-contain" : "h-24 md:h-32 w-auto object-contain"}
              style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined}
            />
          </div>

          {/* Main Message */}
          <div className="space-y-4">
            <h1 className="showcase-heading text-3xl md:text-4xl font-bold text-foreground">
              Showcase Unavailable
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              This showcase page is no longer publicly available.
            </p>
            <p className="text-base text-muted-foreground">
              If you believe this is an error, please contact the organization directly.
            </p>
          </div>

          {/* Footer */}
          <div className="pt-8 border-t border-border">
            <a 
              href="https://www.snapshot.live" 
              target="_blank" 
              rel="noopener noreferrer"
              className="showcase-link inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="text-sm">Powered by Snapshot Live</span>
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M14 5l7 7m0 0l-7 7m7-7H3" 
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    );
  }

  const { organization, devices } = data;

  // Optional access code gate — show the code panel before any cameras
  if (isGated) {
    return (
      <ShowcaseAccessGate
        organizationId={organization.id}
        organizationName={organization.name}
        organizationLogoUrl={isWhiteLabel && brand?.config.logo_url ? brand.config.logo_url : organization.logo_url}
        logoScale={isWhiteLabel ? brand?.config.logo_scale : undefined}
        expectedCode={expectedAccessCode}
        onUnlocked={() => setUnlocked(true)}
      />
    );
  }

  const filteredDevices = filterAndSortPublicDevices(devices, filters);
  const desktopGridColumns = {
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
  }[gridColumns] || 'lg:grid-cols-3';
  
  // Filter devices with valid locations for map
  const devicesWithLocation = devices.filter(device => {
    const hasValidLat = device.lat_wgs84 !== null && 
                        !isNaN(device.lat_wgs84) && 
                        device.lat_wgs84 >= -90 && 
                        device.lat_wgs84 <= 90;
    const hasValidLng = device.long_wgs84 !== null && 
                        !isNaN(device.long_wgs84) && 
                        device.long_wgs84 >= -180 && 
                        device.long_wgs84 <= 180;
    return hasValidLat && hasValidLng;
  });

  // Handle device click from map
  const handleMapDeviceClick = (deviceId: string) => {
    const device = devices.find(d => d.device_id === deviceId);
    if (device) {
      setSelectedDevice(device);
      setIsModalOpen(true);
    }
  };
  
  console.log('🎬 [OrganizationShowcase] Rendering success state:', {
    organizationName: organization?.name,
    devicesCount: devices?.length,
    filteredCount: filteredDevices?.length,
    devicesWithLocation: devicesWithLocation?.length,
    deviceIds: devices?.map(d => d.device_id)
  });

  return (
    <div className="brand-showcase min-h-screen bg-background text-foreground">
      {/* Professional Header */}
      <header className="showcase-surface border-b bg-background shadow-sm">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              {(isWhiteLabel ? brand?.config.logo_url : organization.logo_url) && (
                <img
                  src={(isWhiteLabel ? brand?.config.logo_url : organization.logo_url) || ""}
                  alt={`${organization.name} logo`}
                  className={isWhiteLabel ? "brand-showcase-header-logo object-contain" : "h-16 w-16 md:h-12 md:w-12 object-contain rounded-lg"}
                  style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined}
                />
              )}
              <div>
                <h1 className="showcase-heading text-2xl md:text-3xl font-bold tracking-tight text-center">
                  {organization.name}
                </h1>
              </div>
            </div>
            
            {organization.website && (
              <a
                href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="showcase-link text-sm hover:underline md:absolute md:right-0"
              >
                Visit Website
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Hero/Location Image Section */}
      {organization.marketing_banner_url && (
        <section className="showcase-surface bg-background border-b">
          <div className="container mx-auto px-4 md:px-8 py-4 md:py-6">
            <div className="relative w-full overflow-hidden rounded-lg shadow-sm">
              <div className="aspect-[16/9] md:aspect-[21/9]">
                <img
                  src={organization.marketing_banner_url}
                  alt={`${organization.name} location`}
                  className="object-cover w-full h-full"
                  loading="lazy"
                  onError={(e) => {
                    // Hide the entire section if image fails to load
                    const section = e.currentTarget.closest('section');
                    if (section) section.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Organization Description */}
      {organization.description && (
        <div className="showcase-surface border-b bg-background">
          <div className="container mx-auto px-4 md:px-8 py-4">
            <p className="text-center text-muted-foreground max-w-3xl mx-auto whitespace-pre-line">
              {organization.description}
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-6">
        {devices.length === 0 ? (
          <div className="showcase-panel text-center py-16 bg-background rounded-lg border">
            <Camera className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg">
              No cameras available at this time.
            </p>
          </div>
        ) : (
          <>
            {/* Page Title and Grid Selector */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <h2 className="showcase-heading text-xl font-semibold mb-1">
                  Available Snapshot Cameras
                </h2>
                <p className="text-muted-foreground text-sm">
                  {filteredDevices.length} {filteredDevices.length === 1 ? 'camera' : 'cameras'} {filters.search ? 'found' : 'available'}
                </p>
              </div>
              {filteredDevices.length >= 3 && (
                <GridLayoutSelector value={gridColumns} onValueChange={setGridColumns} />
              )}
            </div>

            {/* Filters */}
            <PublicDeviceFilters filters={filters} onFiltersChange={setFilters} />

            {/* Device Grid */}
            {filteredDevices.length === 0 ? (
              <div className="showcase-panel text-center py-16 bg-background rounded-lg border">
                <Camera className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg">
                  No cameras found matching "{filters.search}"
                </p>
              </div>
            ) : (
              <div
                className={`grid grid-cols-1 gap-4 ${
                  filteredDevices.length === 1
                    ? 'max-w-md mx-auto'
                    : filteredDevices.length === 2
                      ? 'md:grid-cols-2'
                      : `md:grid-cols-2 ${desktopGridColumns}`
                }`}
              >
                {filteredDevices.map((device, index) => {
                  console.log(`🎬 [OrganizationShowcase] Rendering device ${index + 1}/${filteredDevices.length}:`, device.device_name);
                  return (
                    <ErrorBoundary key={device.device_id} componentName={`PublicDeviceTile-${device.device_name}`}>
                      <div id={`device-${device.device_id}`}>
                        <PublicDeviceTile device={device} organizationId={organizationId || ''} />
                      </div>
                    </ErrorBoundary>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Interactive Device Map Section */}
      {devicesWithLocation.length > 0 && (
        <section className="showcase-surface bg-background border-t">
          <div className="container mx-auto px-4 md:px-8 py-6">
            <h2 className="showcase-heading text-xl font-semibold mb-4">
              Camera Locations
            </h2>
            <ShowcaseDeviceMap
              devices={devicesWithLocation}
              onDeviceClick={handleMapDeviceClick}
            />
          </div>
        </section>
      )}

      {/* Feedback Button */}
      <ShowcaseFeedbackButton 
        organizationId={organizationId || ""} 
        organizationName={organization.name}
      />

      {/* Device Detail Modal */}
      <PublicDeviceDetailModal
        device={selectedDevice}
        organizationId={organizationId || ""}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />

      {/* Footer */}
      <footer className="showcase-surface border-t bg-background mt-12 py-6">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {organization.name}. All rights reserved.</p>
            <div className="inline-flex items-center gap-2">
              <span>Powered by</span>
              <a 
                href="https://www.snapshot.live"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                aria-label="Visit Snapshot Live website"
              >
                <img 
                  src="/lovable-uploads/37267525-cc1e-42cd-a617-eb98609d4050.png" 
                  alt="Snapshot Live"
                  className="h-6 object-contain"
                  style={{ filter: "brightness(0)" }}
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function OrganizationShowcase() {
  return (
    <ErrorBoundary componentName="OrganizationShowcase">
      <OrganizationShowcaseContent />
    </ErrorBoundary>
  );
}
